import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from 'reactstrap';
import { SelectField } from 'components/Common/fields';

const FALLBACK_STATUSES = ['Active', 'Dormant', 'Defaulted', 'Inactive', 'Deceased'];
const FALLBACK_REASONS = ['Client Request', 'Management Discretion', 'Data Correction'];

const STATUS_HELP = {
  Active: 'Member is in good standing.',
  Dormant: 'Lapsed but recoverable; wallet floor -300.',
  Defaulted: 'Lapsed; wallet floor 0. Included in default reminder SMS.',
  Inactive: 'Frozen - excluded from automatic processing.',
  Deceased: 'Stops notifications and freezes recalculation for this member.',
};

/**
 * Admin-only durable override of member + policy status (old portal parity).
 * `info` is the GET /status-override payload.
 */
const StatusOverrideModal = ({ isOpen, toggle, info, onSubmit, submitting }) => {
  const [memberStatus, setMemberStatus] = useState('');
  const [policyStatus, setPolicyStatus] = useState('');
  const [matured, setMatured] = useState(true);
  const [mode, setMode] = useState('FULL');
  const [reason, setReason] = useState('');
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (isOpen) {
      setMemberStatus(info?.currentMemberStatus || '');
      setPolicyStatus(info?.currentPolicyStatus || info?.currentMemberStatus || '');
      setMatured(true);
      setMode(info?.overrideMode === 'STATUS_ONLY' ? 'STATUS_ONLY' : 'FULL');
      setReason('');
      setComments('');
    }
  }, [isOpen, info]);

  const statusOptions = info?.availableStatuses?.length ? info.availableStatuses : FALLBACK_STATUSES;
  const reasonOptions = info?.reasonOptions?.length ? info.reasonOptions : FALLBACK_REASONS;
  const isEnabled = !!memberStatus && !!policyStatus && !!reason && !submitting;

  const submit = (e) => {
    e.preventDefault();
    if (!isEnabled) return;
    onSubmit({ memberStatus, policyStatus, matured, mode, reason, comments });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Override Status</ModalHeader>
      <ModalBody>
        <Form onSubmit={submit} autoComplete="off">
          <Row>
            <Col lg="6">
              <FormGroup className="mb-3">
                <Label for="memberStatus">Member Status</Label>
                <SelectField id="memberStatus" name="memberStatus"
                  value={memberStatus} onChange={(e) => setMemberStatus(e.target.value)}>
                  <option value="">Select member status...</option>
                  {statusOptions.map((s) => (<option key={s} value={s}>{s}</option>))}
                </SelectField>
                {STATUS_HELP[memberStatus] && (
                  <small className="text-muted">{STATUS_HELP[memberStatus]}</small>
                )}
              </FormGroup>
            </Col>
            <Col lg="6">
              <FormGroup className="mb-3">
                <Label for="policyStatus">Policy Status</Label>
                <SelectField id="policyStatus" name="policyStatus"
                  value={policyStatus} onChange={(e) => setPolicyStatus(e.target.value)}>
                  <option value="">Select policy status...</option>
                  {statusOptions.map((s) => (<option key={s} value={s}>{s}</option>))}
                </SelectField>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup check className="mb-3">
            <Input type="checkbox" id="matured" checked={matured} onChange={(e) => setMatured(e.target.checked)} />
            <Label for="matured" check>Policy Matured</Label>
          </FormGroup>

          <FormGroup className="mb-3">
            <Label>Recalculation</Label>
            <FormGroup check>
              <Input type="radio" name="mode" id="modeFull" value="FULL"
                checked={mode === 'FULL'} onChange={() => setMode('FULL')} />
              <Label for="modeFull" check>
                Freeze wallet recalculation <span className="text-muted">(recommended)</span>
                <br /><small className="text-muted">Status, maturity and wallet are all frozen until cleared.</small>
              </Label>
            </FormGroup>
            <FormGroup check className="mt-2">
              <Input type="radio" name="mode" id="modeStatusOnly" value="STATUS_ONLY"
                checked={mode === 'STATUS_ONLY'} onChange={() => setMode('STATUS_ONLY')} />
              <Label for="modeStatusOnly" check>
                Status only
                <br /><small className="text-muted">Pin status &amp; maturity, but keep recalculating the wallet from payments.</small>
              </Label>
            </FormGroup>
          </FormGroup>

          <FormGroup className="mb-3">
            <Label for="reason">Reason</Label>
            <SelectField id="reason" name="reason"
              value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">Select a reason...</option>
              {reasonOptions.map((r) => (<option key={r} value={r}>{r}</option>))}
            </SelectField>
          </FormGroup>

          <FormGroup className="mb-3">
            <Label for="comments">Comments</Label>
            <Input type="textarea" id="comments" name="comments" rows={3}
              placeholder="Optional notes..." value={comments} onChange={(e) => setComments(e.target.value)} />
          </FormGroup>

          <p className="text-warning mb-0">
            <small>
              {mode === 'STATUS_ONLY'
                ? 'Status & maturity are pinned manually; the wallet keeps recalculating from payments until the override is cleared.'
                : 'This is a manual override. Wallet recalculation will NOT auto-update this member until an admin clears the override.'}
            </small>
          </p>

          <FormGroup className="mt-3">
            <Button type="submit" color="primary" block disabled={!isEnabled}>
              {submitting ? 'Processing...' : 'Apply Override'}
            </Button>
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={submitting}>Cancel</Button>
      </ModalFooter>
    </Modal>
  );
};

export default StatusOverrideModal;
